import { useEffect, useMemo, useState } from "react";
import { MultiSelect } from "primereact/multiselect";
import { Chip } from "primereact/chip";
import { Skeleton } from "primereact/skeleton";
import { Tooltip } from "primereact/tooltip";

import { getSvcSettingsClient } from "sj-ui-toolkit/src/client/svcSettings";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { C, themeClasses } from "sj-ui-toolkit/src/atlas/utils";

import { loadAllAssignmentsByFilter } from "@/api/assignments";

import S from "./ExternalAccessControl.module.scss";

const SETTINGS_GROUP_NAME = "external.access.control";
const SETTINGS_FIELD_NAME = "requiredAssignments";

type AssignmentStatus = "active" | "inactive" | "deleted";

interface AssignmentOption {
  label: string;
  value: string;
  disabled: boolean;
  status: AssignmentStatus;
}

const ASSIGNMENT_STATUS_TOOLTIP: Record<Exclude<AssignmentStatus, "active">, string> = {
  inactive:
    "This assignment is inactive and can't be worked on by learners — reactivate it, or remove it from the required list.",
  deleted:
    "This assignment no longer exists. It can't be worked on by learners — remove it from the required list.",
};

export const ExternalAccessControl = () => {
  const currentTenantName = getCurrentSelectedTenant() ?? "";
  const { showToast } = useToast();

  const [options, setOptions] = useState<AssignmentOption[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!currentTenantName) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const load = async () => {
      try {
        // filter: "all" so a deactivated-but-selected assignment still resolves to a name.
        const [assignments, settingsData] = await Promise.all([
          loadAllAssignmentsByFilter(currentTenantName, "all"),
          getSvcSettingsClient().getTenantSettings(currentTenantName, SETTINGS_GROUP_NAME).promise,
        ]);
        if (cancelled) return;

        setOptions(
          assignments
            .filter((assignment): assignment is { id: string; name: string; activated?: boolean } =>
              Boolean(assignment.id && assignment.name)
            )
            .map(assignment => ({
              label:
                assignment.activated === false ? `${assignment.name} (Inactive)` : assignment.name,
              value: assignment.id,
              disabled: assignment.activated === false,
              status: (assignment.activated === false ? "inactive" : "active") as AssignmentStatus,
            }))
        );

        const group = (settingsData?.groups ?? []).find(g => g.groupName === SETTINGS_GROUP_NAME);
        const rawIds =
          (group?.values?.[SETTINGS_FIELD_NAME] as (string | number)[] | undefined) ?? [];
        setSelectedIds(rawIds.map(String));
      } catch (error) {
        console.error("Error loading compliance gate assignments:", error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [currentTenantName]);

  const selectedOptions = useMemo(
    () =>
      selectedIds.map(
        id =>
          options.find(option => option.value === id) ?? {
            label: `Deleted assignment (${id})`,
            value: id,
            disabled: false,
            status: "deleted" as AssignmentStatus,
          }
      ),
    [selectedIds, options]
  );

  const handleChange = async (nextSelectedIds: string[]) => {
    if (!currentTenantName) return;

    const previousSelectedIds = selectedIds;
    setSelectedIds(nextSelectedIds);

    try {
      const { promise } = getSvcSettingsClient().updateTenantSettings(
        currentTenantName,
        SETTINGS_GROUP_NAME,
        { [SETTINGS_FIELD_NAME]: nextSelectedIds.map(Number).filter(id => !Number.isNaN(id)) }
      );
      await promise;
      showToast({
        severity: "success",
        summary: "Success",
        detail: "Compliance gate assignments updated successfully",
      });
    } catch (error) {
      console.error("Error updating compliance gate assignments:", error);
      setSelectedIds(previousSelectedIds);
      showToast({
        severity: "error",
        summary: "Error",
        detail: "Failed to update compliance gate assignments. Please try again.",
      });
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor="externalAccessControl" className="atlas-h5 flex items-center gap-2 m-0">
        Required Assignments
        <i
          className={C("pi pi-info-circle text-base cursor-help", themeClasses.textPrimary)}
          data-pr-tooltip="Users must complete these assignments to pass a compliance gate check. This is used to block Pull/Merge requests via the Aspen Connector for GitHub/GitLab."
          data-pr-position="right"
        />
      </label>
      {isLoading ? (
        <Skeleton width="100%" height="36px" />
      ) : (
        <>
          <MultiSelect
            id="externalAccessControl"
            value={selectedIds}
            options={options}
            optionLabel="label"
            optionValue="value"
            optionDisabled="disabled"
            onChange={e => handleChange((e.value as string[] | null) ?? [])}
            maxSelectedLabels={0}
            selectedItemsLabel="{0} assignments selected"
            filter
            showClear
            placeholder="Select required assignments"
            className="w-full"
          />
          {selectedOptions.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedOptions.map(option => (
                <Chip
                  key={option.value}
                  label={option.label}
                  icon={option.status === "active" ? undefined : "pi pi-exclamation-triangle"}
                  removable
                  onRemove={() => {
                    void handleChange(selectedIds.filter(id => id !== option.value));
                    return false;
                  }}
                  className={C("assignment-chip", S.chip)}
                  pt={
                    option.status === "active"
                      ? undefined
                      : { icon: { className: S.chipWarningIcon } }
                  }
                  data-pr-tooltip={
                    option.status === "active"
                      ? undefined
                      : ASSIGNMENT_STATUS_TOOLTIP[option.status]
                  }
                  data-pr-position="top"
                />
              ))}
            </div>
          )}
          <Tooltip target=".assignment-chip" className="max-w-xs" />
        </>
      )}
    </div>
  );
};
